import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  signInAnonymously,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, staffAuth } from '../firebase.js';

export const useAuthStore = defineStore('pos-auth', () => {
  // ── Device identity (anonymous Firebase auth + posDevice claim) ──────────
  const deviceUser = ref(null);
  const deviceClaims = ref({});

  // ── Staff identity (short-lived custom token from posStaffPinAuth) ───────
  const staffUser = ref(null);
  const staffClaims = ref({});
  // Kept in memory only — never persisted to avoid security risk on shared device.
  const _staffPin = ref(null);
  const staffTokenExpiresAt = ref(null);

  // ── Derived state ─────────────────────────────────────────────────────────
  const isPaired = computed(() => !!deviceClaims.value?.posDevice);
  const isStaffAuthenticated = computed(() => {
    if (!staffUser.value) return false;
    if (!staffTokenExpiresAt.value) return true;
    return Date.now() < staffTokenExpiresAt.value;
  });

  const staffName = computed(() => staffClaims.value?.name ?? null);
  const staffRole = computed(() => staffClaims.value?.role ?? null);
  const staffId = computed(() => staffUser.value?.uid ?? null);

  // ── Bootstrap: sign in anonymously on first load ─────────────────────────
  // Resolved once initDevice() finishes — router guards await this.
  let _resolveInit;
  const initPromise = new Promise(resolve => { _resolveInit = resolve; });

  async function initDevice() {
    // Firebase Auth restores the persisted user asynchronously. Wait for the
    // first auth-state event before reading auth.currentUser — otherwise we'd
    // see null and create a fresh anonymous user that loses the posDevice claim.
    await new Promise(resolve => {
      const unsub = onAuthStateChanged(auth, () => { unsub(); resolve(); });
    });

    // Only accept anonymous users as the device identity. Non-anonymous users
    // are staff tokens that leaked into primary auth before the two-instance fix
    // was deployed. Sign them out so a clean anonymous device identity is created.
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      await signOut(auth);
    }

    if (auth.currentUser) {
      deviceUser.value = auth.currentUser;
      const token = await auth.currentUser.getIdTokenResult();
      deviceClaims.value = token.claims;
    } else {
      const cred = await signInAnonymously(auth);
      deviceUser.value = cred.user;
      const token = await cred.user.getIdTokenResult();
      deviceClaims.value = token.claims;
    }
    _resolveInit();
  }

  // Called by posDevicePair Function result — re-read claims after pairing.
  async function refreshDeviceClaims() {
    const token = await auth.currentUser.getIdTokenResult(true);
    deviceClaims.value = token.claims;
    deviceUser.value = auth.currentUser;
  }

  // ── Staff PIN authentication ──────────────────────────────────────────────
  async function authenticateStaff(customToken, staffInfo, pin) {
    // staffInfo: { staffId, name, role, expiresAt }
    // Uses staffAuth (separate Firebase App) so the device anonymous user on the
    // primary auth instance is never overwritten — prevents isPaired=false on reopen.
    staffUser.value = await signInWithCustomToken(staffAuth, customToken).then(c => c.user);
    staffClaims.value = staffInfo;
    staffTokenExpiresAt.value = staffInfo.expiresAt ? new Date(staffInfo.expiresAt).getTime() : null;
    _staffPin.value = pin;
  }

  // Silent token refresh for long shifts — called before token expiry.
  async function refreshStaffToken(getFreshToken) {
    if (!_staffPin.value) return false;
    try {
      const result = await getFreshToken(_staffPin.value);
      await authenticateStaff(result.token, result.staffInfo, _staffPin.value);
      return true;
    } catch {
      _staffPin.value = null;
      staffUser.value = null;
      staffClaims.value = {};
      staffTokenExpiresAt.value = null;
      return false;
    }
  }

  // ── Silent token refresh scheduler ───────────────────────────────────────
  let _refreshTimer = null;

  function scheduleTokenRefresh(callPinAuth) {
    clearTimeout(_refreshTimer);
    if (!staffTokenExpiresAt.value) return;
    // Refresh 5 minutes before expiry.
    const delay = staffTokenExpiresAt.value - Date.now() - 5 * 60 * 1000;
    if (delay <= 0) return;
    _refreshTimer = setTimeout(async () => {
      if (!_staffPin.value) return; // pin was cleared — user must re-authenticate
      await refreshStaffToken(callPinAuth);
      // Re-schedule for the next cycle.
      if (staffTokenExpiresAt.value) scheduleTokenRefresh(callPinAuth);
    }, delay);
  }

  function signOutStaff() {
    clearTimeout(_refreshTimer);
    staffUser.value = null;
    staffClaims.value = {};
    staffTokenExpiresAt.value = null;
    _staffPin.value = null;
    // Device auth (primary auth instance) is untouched — anonymous device user
    // remains signed in, Firestore rules continue to work.
  }

  return {
    deviceUser, deviceClaims,
    staffUser, staffClaims, staffTokenExpiresAt,
    isPaired, isStaffAuthenticated,
    staffName, staffRole, staffId,
    initPromise, initDevice, refreshDeviceClaims,
    authenticateStaff, refreshStaffToken, scheduleTokenRefresh, signOutStaff,
  };
});
